import React, { MutableRefObject, useCallback, useEffect, useState } from 'react';
import { Alert, Grid, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StepId } from '../intentConfig/types';
import { OrderPageData, VoucherStatus, VoucherType } from '../../api';
import DetailsStep from '../steps/DetailsStep';
import VoucherSummary from '../steps/VoucherSummary';
import { MultiLocationDetails } from './MultiLocationDetails';
import DateAndTimeStep from '../steps/dateAndTimeStep/DateAndTimeStep';
import { PersonalDetailsStep } from '../steps/PersonalDetailsStep';
import DisclaimersStep from '../steps/DisclaimersStep';
import ProjectSelectionStep from '../steps/ProjectSelectionStep';
import GiftCardAmountStep from '../steps/GiftCardAmountStep';
import GiftCardTypeStep from '../steps/GiftCardTypeStep';
import PaymentStep from '../steps/PaymentStep';
import VoucherRedemptionSummary from '../steps/VoucherRedemptionSummary';
import PassSummaryStep from '../steps/PassSummaryStep';
import PassScheduleStep from '../steps/PassScheduleStep';
import PassDatedSessionsStep from '../steps/PassDatedSessionsStep';
import { OrderPageTypography } from '../themedComponents/OrderPageTypography';
import { asOrderPageData } from '../core/types';
import { useOrderPage } from '../orderPageContext/useOrderPage';
import { useIntentConfig } from '../intentConfig/useIntentConfig';
import { useLanguage } from '../../utils/GeneralUtils';
import { calculateShouldHideWeekends } from '../hooks/OrderPageErrors';
import { customerFacingPriceFormatter } from '../utils';

type StepRendererProps = {
  formRef: MutableRefObject<any>;
  setShouldDisableNext: (v: boolean) => void;
};

const StepRenderer = ({ formRef, setShouldDisableNext }: StepRendererProps) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const language = useLanguage();
  const { t } = useTranslation();
  const { orderPageState, setOrderPageState, setTime } = useOrderPage();
  const { currentStepId, currentStepIndex } = useIntentConfig();
  const [filteredAvailableDates, setFilteredAvailableDates] = useState(orderPageState.availableDates);

  useEffect(() => {
    setFilteredAvailableDates(orderPageState.availableDates);
  }, [orderPageState.availableDates]);

  const setDateTimeAndPrice = useCallback(
    ({
      newDate,
      newTime,
      newPrice,
      newCourseId,
    }: {
      newDate?: string | null;
      newTime?: number | null;
      newPrice?: number;
      newCourseId?: string;
    }) =>
      setOrderPageState({
        ...orderPageState,
        dateAndTimeDetails: {
          date: newDate ?? null,
          time: newTime,
          courseId: newCourseId,
        },
        details: {
          ...orderPageState.details,
          price: newPrice || orderPageState.details.price,
        },
      }),
    [orderPageState, setOrderPageState],
  );

  const { orderPage, specificOrderPage, voucher, orderPageIntent } = orderPageState;
  const specificEvent = !!orderPageState?.specificEvent;
  const shouldShowVoucherFlow =
    voucher && voucher.status === VoucherStatus.ACTIVE && voucher.type !== VoucherType.MONETARY;
  const vouchersConfig = (orderPage as OrderPageData).effective_vouchers_config;
  const shouldHideWeekends = calculateShouldHideWeekends(orderPageState);
  const bookableOrderPage = asOrderPageData(orderPage);
  const shouldHideLocationAndDateTimeStep =
    (currentStepId !== StepId.DETAILS && currentStepId !== StepId.DATE_AND_TIME) ||
    specificOrderPage?.onlyVouchers ||
    specificEvent;
  const waitingListUrl = orderPageState.experience
    ? `/waiting-list/${orderPageState.account.id}/${orderPageState.experience.id}/${orderPage.id}/${
        orderPageState.details?.persons || 1
      }`
    : '';

  return (
    <Grid container wrap="nowrap" mt={currentStepIndex !== 0 ? 0 : isSmallScreen ? 1 : 3} flexDirection="column">
      {currentStepId === StepId.DETAILS && orderPageState.prefilledVoucherCode && voucher && (
        <Grid item mb={2}>
          <Alert severity="info">
            {t('vouchers.monetaryBanner', {
              balance: customerFacingPriceFormatter(voucher.balance ?? 0, orderPageState.account),
            })}
          </Alert>
        </Grid>
      )}

      {(currentStepId === StepId.DETAILS || currentStepId === StepId.VOUCHER_SUMMARY) && (
        <Grid item>{shouldShowVoucherFlow ? <VoucherSummary voucher={voucher} /> : <DetailsStep errors={{}} />}</Grid>
      )}

      {!shouldHideLocationAndDateTimeStep && bookableOrderPage && (
        <Grid>
          <MultiLocationDetails setFilteredAvailableDates={setFilteredAvailableDates} />
          <DateAndTimeStep
            account={orderPageState.account}
            initialAvailableDates={orderPageState.availableDates}
            locations={orderPageState.account.locations}
            setShouldDisableNext={setShouldDisableNext}
            availableDates={filteredAvailableDates}
            setTime={setTime}
            shouldHideWeekends={shouldHideWeekends}
            orderPageIntent={orderPageIntent}
            orderPage={bookableOrderPage}
            date={orderPageState.dateAndTimeDetails.date}
            time={orderPageState.dateAndTimeDetails.time}
            isLoadingDates={orderPageState.isLoadingDates}
            waitingListUrl={waitingListUrl}
            orderDetails={orderPageState.details}
            setDateTimeAndPrice={setDateTimeAndPrice}
            troubleshoot={specificOrderPage?.troubleshoot}
            specificEvent={specificEvent}
            preSelectedDate={specificOrderPage?.preSelectedDate}
          />
        </Grid>
      )}

      {(currentStepId === StepId.PERSONAL_DETAILS || currentStepId === StepId.GIFT_CARD_PERSONAL_DETAILS) && (
        <Grid item>
          <PersonalDetailsStep formRef={formRef} />
        </Grid>
      )}

      {currentStepId === StepId.GIFT_CARD_TYPE && (
        <Grid item>
          <GiftCardTypeStep />
        </Grid>
      )}

      {currentStepId === StepId.GIFT_CARD_AMOUNT && (
        <Grid item>
          <GiftCardAmountStep setShouldDisableNext={setShouldDisableNext} />
        </Grid>
      )}

      {currentStepId === StepId.PROJECT_SELECTION && (
        <Grid item>
          <ProjectSelectionStep setShouldDisableNext={setShouldDisableNext} />
        </Grid>
      )}

      {currentStepId === StepId.DISCLAIMERS && (
        <Grid item>
          <DisclaimersStep />
        </Grid>
      )}

      {specificOrderPage?.onlyVouchers &&
        !specificOrderPage?.standaloneGiftCard &&
        currentStepIndex === 0 &&
        vouchersConfig?.voucher_widget_title && (
          <Grid container flexDirection="column">
            <OrderPageTypography variant={'h3'}>{vouchersConfig?.voucher_widget_title?.[language]}</OrderPageTypography>
            <OrderPageTypography variant={'h6'}>
              {vouchersConfig?.voucher_widget_subtitle?.[language]}
            </OrderPageTypography>
          </Grid>
        )}

      {currentStepId === StepId.PASS_SUMMARY && (
        <Grid item>
          <PassSummaryStep />
        </Grid>
      )}

      {currentStepId === StepId.PASS_SCHEDULE_SELECTIONS && (
        <Grid item>
          <PassScheduleStep setShouldDisableNext={setShouldDisableNext} />
        </Grid>
      )}

      {currentStepId === StepId.PASS_DATED_SESSION_SELECTIONS && (
        <Grid item>
          <PassDatedSessionsStep setShouldDisableNext={setShouldDisableNext} />
        </Grid>
      )}

      {currentStepId === StepId.PAYMENT && <PaymentStep />}
      {currentStepId === StepId.REDEMPTION_SUMMARY && <VoucherRedemptionSummary />}
    </Grid>
  );
};

export default StepRenderer;
